import { Popover } from "@base-ui-components/react/popover";
import { Check, ChevronsUpDown, LogOut, Monitor, Moon, Plus, Sun } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useAuthSession } from "@/app/auth/session";
import { Button } from "@/app/components/ui";
import { type ThemeMode, useTheme } from "@/app/theme/ThemeProvider";
import { cn } from "@/lib/utils";

const THEME_OPTIONS: { value: ThemeMode; label: string; icon: typeof Sun }[] = [
	{ value: "light", label: "Light", icon: Sun },
	{ value: "dark", label: "Dark", icon: Moon },
	{ value: "system", label: "System", icon: Monitor },
];

function Tile({ name, className }: { name: string | undefined; className?: string }) {
	return (
		<span
			className={cn(
				"flex shrink-0 items-center justify-center rounded-sm bg-primary/15 font-mono text-[11px] font-semibold uppercase text-primary",
				className,
			)}
		>
			{name?.charAt(0).toUpperCase() ?? "?"}
		</span>
	);
}

export function WorkspaceMenu() {
	const navigate = useNavigate();
	const { activeWorkspace, workspaces, switchWorkspace, user, signOut } = useAuthSession();
	const { theme, setTheme } = useTheme();
	const [open, setOpen] = useState(false);

	return (
		<Popover.Root open={open} onOpenChange={setOpen}>
			<Popover.Trigger
				className="flex w-full items-center gap-2.5 rounded-sm px-1.5 py-1.5 text-left transition-colors duration-150 hover:bg-sidebar-accent data-[open]:bg-sidebar-accent"
				aria-label="Workspace menu"
			>
				<Tile name={activeWorkspace?.name} className="size-7" />
				<span className="min-w-0 flex-1 leading-tight">
					<span className="block truncate text-[13px] font-medium text-sidebar-accent-foreground">
						{activeWorkspace?.name ?? "No workspace"}
					</span>
					<span className="label-micro text-[9px] text-sidebar-muted">
						{activeWorkspace?.role ?? "Workspace"}
					</span>
				</span>
				<ChevronsUpDown className="size-3.5 shrink-0 text-sidebar-muted" />
			</Popover.Trigger>

			<Popover.Portal>
				<Popover.Positioner side="top" align="start" sideOffset={8} className="z-50">
					<Popover.Popup className="w-64 rounded-sm border border-border bg-popover text-popover-foreground shadow-[0_16px_40px_-12px_oklch(0_0_0/0.5)] outline-none transition-all duration-150 ease-out data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[starting-style]:translate-y-1 data-[starting-style]:opacity-0">
						{/* ─── Workspaces ─────────────────────────────────── */}
						<div className="p-1.5">
							<p className="label-micro px-2 pb-1.5 pt-1 text-[10px] text-muted-foreground">
								Workspaces
							</p>
							{workspaces.length === 0 ? (
								<p className="px-2 py-1.5 text-[13px] text-muted-foreground">No workspaces yet</p>
							) : (
								<ul className="max-h-56 space-y-px overflow-y-auto">
									{workspaces.map((workspace) => {
										const isActive = workspace.id === activeWorkspace?.id;
										return (
											<li key={workspace.id}>
												<button
													type="button"
													onClick={() => {
														switchWorkspace(workspace.id);
														setOpen(false);
													}}
													className={cn(
														"flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left transition-colors duration-150 hover:bg-accent",
														isActive && "bg-accent/70",
													)}
												>
													<Tile name={workspace.name} className="size-6" />
													<span className="min-w-0 flex-1 leading-tight">
														<span className="block truncate text-[13px] font-medium text-foreground">
															{workspace.name}
														</span>
														<span className="label-micro text-[9px] text-muted-foreground">
															{workspace.role}
														</span>
													</span>
													{isActive ? <Check className="size-3.5 shrink-0 text-primary" /> : null}
												</button>
											</li>
										);
									})}
								</ul>
							)}
							<button
								type="button"
								onClick={() => {
									navigate("/onboarding/workspace");
									setOpen(false);
								}}
								className="mt-1 flex w-full items-center gap-2.5 rounded-sm border border-dashed border-border px-2 py-2 text-[13px] text-muted-foreground transition-colors duration-150 hover:border-muted-foreground/40 hover:bg-accent hover:text-foreground"
							>
								<Plus className="size-3.5 shrink-0" />
								New workspace
							</button>
						</div>

						<div className="h-px bg-border" />

						{/* ─── Theme ──────────────────────────────────────── */}
						<div className="p-1.5">
							<p className="label-micro px-2 pb-1.5 pt-1 text-[10px] text-muted-foreground">
								Theme
							</p>
							<div className="grid grid-cols-3 gap-1 px-1 pb-0.5">
								{THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
									<button
										key={value}
										type="button"
										onClick={() => setTheme(value)}
										aria-pressed={theme === value}
										className={cn(
											"flex flex-col items-center gap-1.5 rounded-sm border px-2 py-2 transition-colors duration-150",
											theme === value
												? "border-primary/40 bg-primary/10 text-primary"
												: "border-transparent text-muted-foreground hover:bg-accent hover:text-foreground",
										)}
									>
										<Icon className="size-3.5" />
										<span className="label-micro text-[9px]">{label}</span>
									</button>
								))}
							</div>
						</div>

						<div className="h-px bg-border" />

						{/* ─── Session ────────────────────────────────────── */}
						<div className="flex items-center gap-2.5 p-3">
							<Tile name={user?.email} className="size-7" />
							<span className="min-w-0 flex-1 leading-tight">
								<span className="block truncate text-[13px] font-medium text-foreground">
									{user?.name ?? "User"}
								</span>
								<span className="block truncate font-mono text-[10px] text-muted-foreground">
									{user?.email ?? "No session"}
								</span>
							</span>
							<Button variant="ghost" size="icon" onClick={signOut} title="Sign out">
								<LogOut />
							</Button>
						</div>
					</Popover.Popup>
				</Popover.Positioner>
			</Popover.Portal>
		</Popover.Root>
	);
}
